/**
 * LearnStage — auto-play voice narrated tour synced with 3D scene.
 * User can pause/resume, skip forward/backward through steps.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Simulation3DCanvas from "@/components/3d/Simulation3DCanvas";
import { Button } from "@/components/ui/button";
import { Play, Pause, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

export const LearnStage = ({ experiment, steps, onComplete }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const mountedRef = useRef(true);
  const sessionRef = useRef(0); // increments on every play/pause/step-change/unmount
  const step = steps[idx];

  const cleanupAudio = () => {
    sessionRef.current += 1; // any in-flight fetch will be discarded
    try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.src = "";
    }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    audioRef.current = null;
  };

  const playStep = async (i) => {
    cleanupAudio();
    const mySession = sessionRef.current;
    setPlaying(true);
    setTtsBusy(true);
    try {
      const voice = localStorage.getItem("brimi_tts_voice") || "shimmer";
      const res = await api.post("/ai/tts", { text: steps[i].text, voice }, { responseType: "blob" });
      // Guard: if the session changed (user clicked next/prev/tab) or component unmounted, drop this audio.
      if (!mountedRef.current || sessionRef.current !== mySession) return;
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        if (!mountedRef.current || sessionRef.current !== mySession) return;
        setPlaying(false);
        if (i + 1 < steps.length) setIdx(i + 1);
        else onComplete?.();
      };
      audio.onerror = () => { if (mountedRef.current) setPlaying(false); };
      await audio.play();
    } catch (err) {
      console.warn("Learn TTS failed:", err?.message || err);
      if (mountedRef.current) setPlaying(false);
    } finally {
      if (mountedRef.current) setTtsBusy(false);
    }
  };

  const pauseStep = () => {
    audioRef.current?.pause();
    setPlaying(false);
  };

  const resumeStep = () => {
    if (audioRef.current) { audioRef.current.play(); setPlaying(true); }
    else playStep(idx);
  };

  // When step changes (via next/prev), auto-play new step
  useEffect(() => {
    mountedRef.current = true;
    playStep(idx);
    return () => cleanupAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Global unmount: stop audio and mark unmounted so late TTS response doesn't restart.
  useEffect(() => {
    const stopOnHide = () => {
      if (document.hidden) {
        try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
        setPlaying(false);
      }
    };
    document.addEventListener("visibilitychange", stopOnHide);
    return () => {
      mountedRef.current = false;
      cleanupAudio();
      document.removeEventListener("visibilitychange", stopOnHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-full w-full flex flex-col lg:flex-row gap-4 min-h-0">
      {/* 3D scene */}
      <div className="w-full lg:flex-1 min-h-[280px] sm:min-h-[360px] lg:min-h-[400px] rounded-2xl border border-slate-800 bg-slate-950 overflow-hidden relative">
        <Simulation3DCanvas experiment={experiment} params={step?.sim_params || {}} hideNarrator />
      </div>

      {/* Narration panel */}
      <div className="w-full lg:w-96 lg:shrink-0 flex flex-col gap-3 min-h-0">
        <div className="rounded-2xl bg-slate-900/80 border border-cyan-500/30 p-5 shadow-xl">
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 text-xs font-black">{idx + 1}</div>
            Step {idx + 1} of {steps.length} · Learn
          </div>
          <h3 className="font-outfit font-black text-2xl text-slate-100 leading-tight" data-testid="learn-step-title">
            {step?.title}
          </h3>
          <p className="mt-3 text-slate-300 leading-relaxed text-sm">
            {step?.text}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-slate-700 text-slate-300"
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            data-testid="learn-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-full"
            onClick={playing ? pauseStep : resumeStep}
            disabled={ttsBusy}
            data-testid="learn-play-pause"
          >
            {playing ? <><Pause className="mr-2 h-4 w-4" />Pause</> : <><Play className="mr-2 h-4 w-4" />Play</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-slate-700 text-slate-300"
            onClick={() => setIdx(Math.min(steps.length - 1, idx + 1))}
            disabled={idx === steps.length - 1}
            data-testid="learn-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mt-1">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-cyan-400" : i < idx ? "w-1.5 bg-cyan-700" : "w-1.5 bg-slate-700"}`}
            />
          ))}
        </div>

        {idx === steps.length - 1 && !playing && (
          <Button
            className="w-full mt-2 bg-emerald-600 hover:bg-emerald-500 rounded-full"
            onClick={() => onComplete?.()}
            data-testid="learn-goto-practice"
          >
            <CheckCircle2 className="mr-2 h-4 w-4" /> I&apos;m ready — start Practice
          </Button>
        )}
      </div>
    </div>
  );
};

export default LearnStage;
